import React, { useState, useEffect, ReactNode } from 'react';
import styles from './HATVKPITable.module.scss';
import Icon from '../../common/Icon/Icon';
import { useAppSelector } from '../../../redux/hooks';
import { useTranslation } from 'react-i18next';
import Modal from '../../common/Modal/Modal';
import LeaderPPVModal from '../../Modals/LeaderPPVModal/LeaderPPVModal';
import { disableEnableScroll } from '../../../utils/disableEnableScroll';
import { useKPIList } from '../../Context/KPIListContext';
import MyAccountsLoader from '../../common/Loaders/MyAccountsLoader/MyAccountsLoader';
import { getCustomShortMonthLabel, useProfileParams } from '../../../utils/common';
import ErrorInPageScreen from '../../common/ErrorInPageScreen/ErrorInPageScreen';
import { useGetLeaderPPVListQuery } from '../../../redux/services/hatvKPIAPI';
import { FormatLocalizedNumber } from '../../../utils/NumberFormatter';
import { RootState } from '../../../redux/store';

interface TableRow {
  label: string;
  values: ValueType[];
}

interface ValueType {
  closingMonth?: string;
  kpiValue: string | number | null | ReactNode;
  kpiFlagCode?: number | null;
  kpiFlagName?: string | null;
}
interface HATVKPIListProps {
  selectedYearMonth: string;
}

const HATVKPITable: React.FC<HATVKPIListProps> = ({ selectedYearMonth }) => {
  const { affiliateCode, aboNumber } = useProfileParams();
  const [selectedMonth, setSelectedMonth] = useState(selectedYearMonth);
  const { kpiListData, isLoadingKPIList, isFetchingKPIList, isKPIListError, isKPIListSuccess } =
    useKPIList();

  const {
    data: leaderPPVData,
    isLoading: isLeaderPPVLoading,
    isFetching: isLeaderPPVFetching,
    isError: isLeaderPPVError,
    refetch: refetchLeaderPPVList,
  } = useGetLeaderPPVListQuery(
    {
      affiliateCode,
      aboNumber,
      closingMonth: selectedMonth,
    },
    {
      skip: !selectedMonth,
    }
  );

  const getFlagColor = (flagCode?: number | null) => {
    if (flagCode === 2) {
      // Yellow
      return currentTheme === 'light' ? '#DA7600' : '#FED773';
    }
    if (flagCode === 3) {
      // Red
      return currentTheme === 'light' ? '#D91734' : '#F16A81';
    }
    return undefined;
  };
  const groupLeaderPpvs = leaderPPVData?.body?.groupLeaderPpvs ?? [];

  const currentTheme = useAppSelector((state: RootState) => state.theme.currentTheme);
  const userDetails = useAppSelector((state: RootState) => state.userDetails.user);
  const [userAffiliateCode] = useState(userDetails?.userAffiliateCode || 0);
  const { t } = useTranslation();
  const isExpanded = useAppSelector((state: RootState) => state.sideNavExpansion.isExpanded);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [isAPIError, setIsAPIError] = useState(false);
  const [modalMonth, setModalMonth] = useState<string | null>(null);
  const handleModalClose = () => {
    if (!modalOpen) {
      refetchLeaderPPVList();
    }
    setModalOpen(false);
    setModalMonth(null);
  };

  useEffect(() => {
    if (
      modalMonth &&
      modalMonth === selectedMonth &&
      !isLeaderPPVFetching &&
      !isLeaderPPVLoading &&
      groupLeaderPpvs.length > 0
    ) {
      setModalOpen(true);
    }
  }, [modalMonth, selectedMonth, isLeaderPPVFetching, isLeaderPPVLoading, groupLeaderPpvs]);

  useEffect(() => {
    disableEnableScroll(modalOpen);
  }, [modalOpen]);

  useEffect(() => {
    const hasNoData = !kpiListData?.body?.qMonthApproval?.length;
    if (isKPIListSuccess && hasNoData) {
      setIsAPIError(true);
    }
  }, [isKPIListSuccess, kpiListData]);

  const isInitialOrLoading =
    isLoadingKPIList ||
    isFetchingKPIList ||
    (!isKPIListSuccess && !Array.isArray(kpiListData?.body?.qMonthApproval));

  if (isInitialOrLoading) {
    return <MyAccountsLoader num={7} hideCustomViewPanel={true} />;
  }

  if (
    isKPIListSuccess &&
    Array.isArray(kpiListData?.body?.qMonthApproval) &&
    kpiListData.body.qMonthApproval.length === 0
  ) {
    return (
      <div className={styles.empty_state_document}>
        <div className={`table_no_data ${styles.empty_state_msg}`}>{t('noAboAvailable')}</div>
      </div>
    );
  }

  if (isKPIListError || isAPIError) {
    return (
      <ErrorInPageScreen
        handleClickTryAgain={() => {
          setIsAPIError(false);
        }}
      />
    );
  }
  const getTableRowsFromKPIListData = (): TableRow[] => {
    if (!kpiListData?.body) return [];
    const { qMonthApproval, hatvKpis } = kpiListData?.body;
    const renderKPIValue = (item: any, underline: boolean = false) => {
      if (!item?.kpiValue) return '-';

      if (item?.kpiFlagCode === 1) {
        return underline ? (
          <div className={styles.flag_value_wrapper}>
            <span
              className={`${styles.kpi_value} ${styles.green} ${underline ? styles.underline_text : ''}`}
            >
              {FormatLocalizedNumber(userAffiliateCode, item.kpiValue)}
            </span>
          </div>
        ) : (
          `${FormatLocalizedNumber(userAffiliateCode, item?.kpiValue)}`
        );
      }

      if (item.kpiFlagCode === 2 || item.kpiFlagCode === 3) {
        const color = getFlagColor(item.kpiFlagCode);
        return (
          <div className={styles.flag_value_wrapper}>
            <span
              className={`${styles.kpi_value} ${styles.red} ${underline ? styles.underline_text : ''}`}
              style={{ color }}
            >
              {FormatLocalizedNumber(userAffiliateCode, item.kpiValue)}
            </span>
            <Icon name="flag" size={14} color={color} className={styles.flag} />
          </div>
        );
      }

      return `${FormatLocalizedNumber(userAffiliateCode, item.kpiValue)}`;
    };

    return [
      {
        label: 'approvedStatus',
        values: (qMonthApproval ?? []).map((item) => ({
          kpiValue:
            typeof item.approvalStatus === 'number'
              ? t(`q_month_approval_status.${item.approvalStatus}`) || '-'
              : '-',
          kpiFlagCode: null,
          kpiFlagName: null,
          closingMonth: item.closingMonth,
        })),
      },
      {
        label: 'hatvGroupSize',
        values: hatvKpis?.groupSize?.map((item) => ({
          ...item,
          kpiValue: renderKPIValue(item),
          closingMonth: item.closingMonth,
        })),
      },
      {
        label: 'hatvContributorCount',
        values: hatvKpis?.contributorCount?.map((item) => ({
          ...item,
          kpiValue: renderKPIValue(item),
          closingMonth: item.closingMonth,
        })),
      },
      {
        label: 'highestPpvInGroup',
        values: hatvKpis?.highestPpvInGroup?.map((item) => ({
          ...item,
          kpiValue: renderKPIValue(item, true),
          closingMonth: item.closingMonth,
        })),
      },
      {
        label: 'volumeFluctuation',
        values: hatvKpis?.volumeFluctuation?.map((item) => ({
          ...item,
          kpiValue: renderKPIValue(item),
          closingMonth: item.closingMonth,
        })),
      },
      {
        label: 'eomGpv',
        values: hatvKpis?.eomGroupPv?.map((item) => ({
          ...item,
          kpiValue: renderKPIValue(item),
          closingMonth: item.closingMonth,
        })),
      },
      {
        label: 'tenure',
        values: hatvKpis?.tenure?.map((item) => ({
          ...item,
          kpiValue:
            item.kpiFlagCode === 1 ? (
              t('no')
            ) : item.kpiFlagCode === 3 ? (
              <>
                <Icon
                  name="flag"
                  size={14}
                  color={getFlagColor(item.kpiFlagCode)}
                  className={`${styles.flag}`}
                />
              </>
            ) : (
              '-'
            ),
          closingMonth: item.closingMonth,
        })),
      },
      {
        label: 'bcrCase',
        values: hatvKpis?.bcrCase?.map((item) => ({
          ...item,
          kpiValue:
            item.kpiFlagCode === 1 ? (
              t('no')
            ) : item.kpiFlagCode === 3 ? (
              <>
                <Icon
                  name="flag"
                  size={14}
                  color={getFlagColor(item.kpiFlagCode)}
                  className={`${styles.flag}`}
                />
              </>
            ) : (
              '-'
            ),
          closingMonth: item.closingMonth,
        })),
      },
    ];
  };

  const tableRows = getTableRowsFromKPIListData();

  return (
    <>
      <div className={`leg_violation_wrapper ${styles.leg_violation_wrapper}`}>
        <div className={`table_outer_wrapper ${styles.table_outer_wrapper}`}>
          <table className={styles.table_wrapper}>
            <thead>
              <tr className={`tbl_header ${styles.tbl_header}`}>
                <th></th>
                {tableRows[0]?.values.map((val, index) => (
                  <th key={index}>
                    {t(`hatv_months.${getCustomShortMonthLabel(val.closingMonth)}`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className={`tbl_body ${styles.tbl_body}`}>
              {tableRows.map((row, index) => (
                <tr
                  key={index}
                  className={`tbl_row ${styles.tbl_row} ${
                    index % 2 === 0 ? `even_row ${styles.even_row}` : `odd_row ${styles.odd_row}`
                  }`}
                >
                  <td>{t(`violation_table.labels.${row.label}`)}</td>
                  {row?.values?.map((val, i) => {
                    const shouldUnderline =
                      row.label === 'highestPpvInGroup' && val?.kpiValue !== '-';
                    return (
                      <td key={i}>
                        {val?.kpiFlagName ? (
                          <div className={`${styles.flag_wrapper}`}>
                            {val?.kpiValue !== null && (
                              <span
                                className={`flag_name ${styles.flag_name} ${shouldUnderline && styles.clickable_data}`}
                                onClick={() => {
                                  if (shouldUnderline) {
                                    setSelectedMonth(val.closingMonth ?? '');
                                    setModalOpen(true);
                                  }
                                }}
                              >
                                {val.kpiValue}
                              </span>
                            )}
                          </div>
                        ) : val?.kpiValue !== null ? (
                          <span
                            className={
                              val.kpiValue === 'Denied' ? `deniend_text ${styles.deniend_text}` : ''
                            }
                            style={{
                              textDecoration: shouldUnderline ? 'underline' : 'none',
                              cursor: shouldUnderline ? 'pointer' : '',
                            }}
                            onClick={() => {
                              if (shouldUnderline) {
                                setSelectedMonth(val.closingMonth ?? '');
                                setModalOpen(true);
                              }
                            }}
                          >
                            {val.kpiValue}
                          </span>
                        ) : (
                          <div className={styles.hyper_arrow_wrapper}>
                            <Icon
                              name="hypen_arrow"
                              size={9}
                              color={currentTheme === 'light' ? '#2C2C2C' : '#D8D8D9'}
                              className={styles.hypen_arrow}
                            />
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          {modalOpen && (
            <Modal showDrawer={modalOpen} onClose={handleModalClose}>
              <LeaderPPVModal
                data={groupLeaderPpvs}
                isLoading={isLeaderPPVFetching || isLeaderPPVLoading}
                isError={isLeaderPPVError}
              />
            </Modal>
          )}
        </div>
      </div>
    </>
  );
};

export default HATVKPITable;
